import React, { useMemo } from 'react';

const HighlightedJDText = ({ text, matched = [], missing = [] }) => {
  const parts = useMemo(() => {
    if (!text) return [];

    // Combine and sort keywords by length descending to match longest phrases first
    const allKeywords = [
      ...(matched || []).filter(k => typeof k === 'string' && k.trim()).map(k => ({ word: k.trim(), type: 'match' })),
      ...(missing || []).filter(k => typeof k === 'string' && k.trim()).map(k => ({ word: k.trim(), type: 'missing' }))
    ].sort((a, b) => b.word.length - a.word.length);

    if (allKeywords.length === 0) {
      return [{ text, type: 'normal' }];
    }

    // Escape regex characters to prevent syntax errors
    const escapeRegExp = (string) => string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

    // \b fails on symbols like "C#" or ".NET".
    // We use lookaround assertions to match spaces, punctuation, or start/end of string.
    const patternStr = allKeywords.map(k => escapeRegExp(k.word)).join('|');
    let pattern;
    try {
      // Lookbehinds are supported in all modern browsers (Safari 16.4+), but we fallback just in case.
      pattern = new RegExp(`(?<=^|\\s|[.,;:!?\\[\\](){}<>"'-])(${patternStr})(?=$|\\s|[.,;:!?\\[\\](){}<>"'-])`, 'gi');
    } catch (e) {
      pattern = new RegExp(`\\b(${patternStr})\\b`, 'gi');
    }

    const result = [];
    let lastIndex = 0;
    
    text.replace(pattern, (match, p1, offset) => {
      // Add standard text before the match
      if (offset > lastIndex) {
        result.push({ text: text.substring(lastIndex, offset), type: 'normal' });
      }
      
      // Identify which type of keyword this is
      const lowerMatch = match.toLowerCase();
      const keywordInfo = allKeywords.find(k => k.word.toLowerCase() === lowerMatch);
      const type = keywordInfo ? keywordInfo.type : 'normal';
      
      result.push({ text: match, type });
      lastIndex = offset + match.length;
      return match;
    });

    // Add any remaining text after the last match
    if (lastIndex < text.length) {
      result.push({ text: text.substring(lastIndex), type: 'normal' });
    }

    return result;
  }, [text, matched, missing]);

  if (!text) return null;

  return (
    <div className="mb-6">
      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">
        🔍 Real-Time JD Match Preview
      </p>
      <div className="bg-gray-50 border border-gray-200 rounded-2xl p-4 sm:p-5 max-h-56 overflow-y-auto custom-scrollbar shadow-inner text-[13px] text-gray-600 leading-relaxed whitespace-pre-wrap font-medium">
        {parts.map((part, i) => {
          if (part.type === 'match') {
            return (
              <span key={i} className="bg-green-100 text-green-800 font-bold px-1.5 py-0.5 rounded-md border border-green-200 shadow-sm mx-0.5">
                {part.text}
              </span>
            );
          } else if (part.type === 'missing') {
            return (
              <span key={i} className="bg-yellow-100 text-yellow-800 font-bold px-1.5 py-0.5 rounded-md border border-yellow-300 shadow-sm mx-0.5">
                {part.text}
              </span>
            );
          }
          return <span key={i}>{part.text}</span>;
        })}
      </div>
      <div className="flex items-center gap-4 mt-2 px-1">
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded bg-green-100 border border-green-200"></div>
          <span className="text-[10px] text-gray-500 font-medium">Already on resume</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded bg-yellow-100 border border-yellow-300"></div>
          <span className="text-[10px] text-gray-500 font-medium">Missing (AI will add)</span>
        </div>
      </div>
    </div>
  );
};

export default HighlightedJDText;
